/**
 * useOptimization - Custom hook for optimization submission and polling
 * 
 * Handles:
 * - NREL REopt job submission
 * - Status polling
 * - Results retrieval
 * - Rate limit tracking
 * - Smart restart detection (same scenario = show running message, different = restart)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';
import { useAuth } from '../auth/useAuth';


const POLL_INTERVAL = 5000; // 5 seconds - fast polling for responsive UX

/**
 * Generate a hash of scenario inputs for comparison
 * Used to detect if user is re-clicking with same or different inputs
 */
function hashScenarioInputs(scenario) {
    if (!scenario) return null;

    // Extract key inputs that would affect optimization results
    const keyInputs = {
        lat: scenario.Site?.latitude,
        lon: scenario.Site?.longitude,
        offGrid: scenario.Site?.off_grid_flag,
        loadSum: scenario.ElectricLoad?.loads_kw?.reduce((a, b) => a + b, 0) || 0,
        loadYear: scenario.ElectricLoad?.year,
        pvMax: scenario.PV?.max_kw,
        pvMin: scenario.PV?.min_kw,
        storageKw: scenario.ElectricStorage?.max_kw,
        storageKwh: scenario.ElectricStorage?.max_kwh,
        genKw: scenario.Generator?.max_kw || scenario.CHP?.max_kw,
        tariff: scenario.ElectricTariff?.blended_annual_energy_rate ||
            (scenario.ElectricTariff?.urdb_response ? 'structured' : null),
        gridImportLimit: scenario.Settings?.grid_import_limit_kw,
        gridExportLimit: scenario.Settings?.grid_export_limit_kw,
    };

    return JSON.stringify(keyInputs);
}

/**
 * Custom hook for optimization workflow
 */
export function useOptimization() {
    const { currentUser } = useAuth();

    // Run state
    const [runUuid, setRunUuid] = useState(null);
    const [lastRunUuid, setLastRunUuid] = useState(null);
    const [manualRunUuid, setManualRunUuid] = useState("");
    const [lastRunInputs, setLastRunInputs] = useState(null);

    // Currently running scenario inputs (for duplicate detection)
    const runningScenarioHashRef = useRef(null);

    // Status
    const [status, setStatus] = useState("");
    const [statusPhase, setStatusPhase] = useState("");  // Current phase from solver
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialDelay, setInitialDelay] = useState(0);

    // Results
    const [outputs, setOutputs] = useState(null);
    const [results, setResults] = useState(null);
    const [dailyData, setDailyData] = useState([]);
    const [isOffGrid, setIsOffGrid] = useState(false);
    const [infeasibilityMetrics, setInfeasibilityMetrics] = useState(null);
    const [bauResults, setBauResults] = useState(null);  // BAU results for caching

    // Rerun indicator - true when inputs have changed since last run
    const [needsRerun, setNeedsRerun] = useState(false);

    // Loading/results state
    const [loadResultsUuid, setLoadResultsUuid] = useState("");
    const [isLoadingResults, setIsLoadingResults] = useState(false);

    // Polling ref
    const pollingRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    /**
     * Check if optimization is currently running
     */
    const isRunning = useCallback(() => {
        return pollingRef.current !== null || isSubmitting;
    }, [isSubmitting]);

    /**
     * Submit a scenario for optimization
     * Handles three cases:
     * 1. No job running -> start new job
     * 2. Job running with SAME inputs -> return message that it's already running (onAlreadyRunning callback)
     * 3. Job running with DIFFERENT inputs -> abort current job and start new one
     */
    const submitScenario = useCallback(async (scenario, options = {}) => {
        const {
            onSuccess,
            onError,
            onAlreadyRunning,  // New callback for when same scenario is already running
            frontendSource = 'react-frontend',
            optimizationEngine = 'nrel_reopt',  // 'nrel_reopt' or 'vortx_flux'
            cachedBauResults = null  // Cached BAU results to skip BAU model run
        } = options;

        // Calculate hash of new scenario inputs
        const newScenarioHash = hashScenarioInputs(scenario);

        // Check if we're currently running an optimization
        if (pollingRef.current !== null || isSubmitting) {
            // Check for admin debug rerun bypass
            const allowDebugRerun = localStorage.getItem('allowDebugRerun') === 'true';

            // Log for debugging
            console.log('🔄 Rerun check:', {
                allowDebugRerun,
                sameHash: runningScenarioHashRef.current === newScenarioHash,
                isPolling: pollingRef.current !== null,
                isSubmitting
            });

            // Compare new inputs to running inputs
            if (runningScenarioHashRef.current === newScenarioHash && !allowDebugRerun) {
                // Same scenario - just notify user it's already running
                console.log('🔄 Same scenario already running, ignoring duplicate click');
                if (onAlreadyRunning) {
                    onAlreadyRunning();
                }
                return { alreadyRunning: true };
            } else {
                // Different scenario - stop current and restart
                console.log('🔄 Different scenario detected, stopping current optimization and restarting');

                // Abort any in-flight requests
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }

                // Stop polling
                if (pollingRef.current) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                }

                // Clear running hash - will be set to new hash below
                runningScenarioHashRef.current = null;
            }
        }

        // Store the hash of the scenario we're about to run
        runningScenarioHashRef.current = newScenarioHash;

        setIsSubmitting(true);
        setStatus("Submitting...");
        setOutputs(null);
        setResults(null);
        setDailyData([]);
        setInfeasibilityMetrics(null);
        setBauResults(null);  // Clear previous BAU results

        // Create abort controller for this request
        abortControllerRef.current = new AbortController();

        try {
            // Build request payload
            const payload = {
                ...scenario,
                optimization_engine: optimizationEngine
            };

            // Include cached BAU if provided
            if (cachedBauResults) {
                payload.cached_bau_results = cachedBauResults;
            }

            // Get Firebase auth token
            const token = currentUser ? await currentUser.getIdToken() : null;

            const response = await fetch(`${API_BASE_URL}/api/site_dna/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Frontend-Source': frontendSource,
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(payload),
                signal: abortControllerRef.current.signal,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `Submission failed: ${response.status}`);
            }

            const uuid = data.run_uuid;
            setRunUuid(uuid);
            setLastRunUuid(uuid);
            setLastRunInputs(scenario);
            setStatus("Submitted - polling for results...");

            // Save to localStorage
            localStorage.setItem("lastRunUuid", uuid);
            localStorage.setItem("lastRunInputs", JSON.stringify(scenario));

            // Start polling
            startPolling(uuid, options);

            if (onSuccess) {
                onSuccess({ runUuid: uuid });
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                setStatus("Submission cancelled");
            } else {
                setStatus(`Error: ${err.message}`);
                if (onError) {
                    onError(err);
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    /**
     * Start polling for job status
     */
    const startPolling = useCallback((uuid, options = {}) => {
        const {
            onComplete,
            onError,
            frontendSource = 'react-frontend',
            transformResults
        } = options;

        // Clear any existing polling
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }

        const poll = async () => {
            try {
                const token = currentUser ? await currentUser.getIdToken() : null;

                const response = await fetch(`${API_BASE_URL}/api/site_dna/status/${uuid}`, {
                    headers: {
                        'X-Frontend-Source': frontendSource,
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                    },
                });

                const data = await response.json();
                const currentStatus = data.status || 'unknown';
                const currentPhase = data.status_phase || '';

                setStatus(currentStatus);
                setStatusPhase(currentPhase);

                // Check for completion
                if (['optimal', 'Optimal Obtained', 'completed'].includes(currentStatus)) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    runningScenarioHashRef.current = null;  // Clear running hash on completion

                    setOutputs(data.outputs || {});
                    setResults(data);

                    // Extract BAU results for caching
                    if (data.bau_results) {
                        setBauResults(data.bau_results);
                    }

                    // Transform results for charts if transformer provided
                    if (transformResults && data) {
                        const transformed = transformResults(data);
                        // Handle both array returns and {data, isOffGrid} object returns
                        if (Array.isArray(transformed)) {
                            setDailyData(transformed);
                        } else if (transformed && transformed.data) {
                            setDailyData(transformed.data);
                            if (transformed.isOffGrid !== undefined) {
                                setIsOffGrid(transformed.isOffGrid);
                            }
                            if (transformed.infeasibilityMetrics) {
                                setInfeasibilityMetrics(transformed.infeasibilityMetrics);
                            }
                        }
                    }

                    // Also check for off-grid from inputs
                    if (data.inputs?.Site?.off_grid_flag) {
                        setIsOffGrid(true);
                    }

                    if (onComplete) {
                        // Include the run_uuid in the data for result persistence
                        onComplete({ ...data, run_uuid: uuid });
                    }
                } else if (['error', 'Error', 'failed', 'Failed', 'timed_out', 'infeasible'].includes(currentStatus)) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    runningScenarioHashRef.current = null;  // Clear running hash on error

                    // Build error message - check for infeasibility first
                    let errorMsg = data.messages?.errors?.join(', ') || data.error || 'Optimization failed';

                    // Check for infeasibility status - indicate grid limit issue without estimate
                    if (currentStatus === 'infeasible') {
                        const metrics = data.infeasibility_metrics || {};
                        setInfeasibilityMetrics({
                            ...metrics,
                            canFindMinimum: true, // Flag to show "Find Minimum" button
                            configured_limit_kw: metrics.configured_limit_kw
                        });

                        const configuredKw = metrics.configured_limit_kw || 'configured';
                        errorMsg = `Grid import limit (${typeof configuredKw === 'number' ? configuredKw.toLocaleString() + ' kW' : configuredKw}) is too restrictive. Use "Find Minimum Limit" to calculate the minimum required.`;
                    } else if (data.infeasibility_metrics) {
                        setInfeasibilityMetrics(data.infeasibility_metrics);
                    }

                    setStatus(`Failed: ${errorMsg}`);

                    if (onError) {
                        onError(new Error(errorMsg));
                    }
                }
                // Otherwise continue polling
            } catch (err) {
                console.error('Polling error:', err);
                // Don't stop polling on transient errors
            }
        };

        // Initial poll
        poll();

        // Start interval
        pollingRef.current = setInterval(poll, POLL_INTERVAL);
    }, []);

    /**
     * Stop polling and clear running state
     */
    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setStatus("Polling stopped");
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        // Clear the running scenario hash so next submission starts fresh
        runningScenarioHashRef.current = null;
    }, []);

    /**
     * Load results for a specific run UUID
     */
    const loadResults = useCallback(async (uuid, options = {}) => {
        const { onComplete, onError, frontendSource = 'react-frontend', transformResults } = options;

        setIsLoadingResults(true);
        setLoadResultsUuid(uuid);

        try {
            const token = currentUser ? await currentUser.getIdToken() : null;

            const response = await fetch(`${API_BASE_URL}/api/site_dna/results/${uuid}`, {
                headers: {
                    'X-Frontend-Source': frontendSource,
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to load results');
            }

            setOutputs(data.outputs || {});
            setResults(data);
            setStatus(data.status || 'loaded');

            if (transformResults && data) {
                const transformed = transformResults(data);
                // Handle both array returns and {data, isOffGrid} object returns
                if (Array.isArray(transformed)) {
                    setDailyData(transformed);
                } else if (transformed && transformed.data) {
                    setDailyData(transformed.data);
                    if (transformed.isOffGrid !== undefined) {
                        setIsOffGrid(transformed.isOffGrid);
                    }
                    if (transformed.infeasibilityMetrics) {
                        setInfeasibilityMetrics(transformed.infeasibilityMetrics);
                    }
                }
            }

            if (data.inputs?.Site?.off_grid_flag) {
                setIsOffGrid(true);
            }

            if (onComplete) {
                onComplete(data);
            }
        } catch (err) {
            setStatus(`Error loading results: ${err.message}`);
            if (onError) {
                onError(err);
            }
        } finally {
            setIsLoadingResults(false);
        }
    }, [currentUser]);

    /**
     * Load saved results from backend storage
     * Used to restore previous optimization results when loading a scenario
     */
    const loadSavedResults = useCallback((savedData, transformResults = null) => {
        if (!savedData || !savedData.results) {
            console.log('No saved results to load');
            return false;
        }

        try {
            const { results: savedResults, runUuid: savedRunUuid, inputsHash: savedInputsHash, inputs: savedInputs } = savedData;

            console.log('Loading saved optimization results:', savedRunUuid);

            // Restore run UUID
            if (savedRunUuid) {
                setLastRunUuid(savedRunUuid);
                setRunUuid(savedRunUuid);
            }

            // Enrich results with degradation and survivability if not already present
            // Note: Vortx Flux uses flat structure (outputs.ElectricStorage), not nested REopt v2 format
            const outputs = savedResults?.outputs || {};
            const batt = outputs.ElectricStorage || {};
            const batterySizeKwh = batt.size_kwh || 0;

            if (batt && batterySizeKwh > 0) {
                // Compute degradation if not present
                if (!batt.degradation) {
                    const discharge = batt.storage_to_load_series_kw || batt.to_load_series_kw || [];
                    const annualThroughput = discharge.reduce((a, b) => a + Math.max(0, b), 0);
                    const fullCycles = batt.size_kwh > 0 ? annualThroughput / batt.size_kwh : 0;

                    // Degradation assumptions (NREL BLAST model - same as solver)
                    // Reference: https://www.nrel.gov/transportation/battery-lifespan
                    const calendarFade = 0.008;  // 0.8% per year (calendar aging)
                    const cycleFade = 0.000015;  // 0.0015% per full cycle
                    const totalAnnualFade = calendarFade + (cycleFade * fullCycles);

                    // Calculate years to 80% capacity
                    let yearsTo80 = null;
                    if (totalAnnualFade > 0) {
                        yearsTo80 = Math.round(Math.log(0.8) / Math.log(1 - totalAnnualFade) * 10) / 10;
                    }

                    // Maintenance cost calculation
                    const maintenanceCostPerKwh = savedInputs?.ElectricStorage?.cycle_cost_per_kwh || 0.005;  // $/kWh default
                    const discountRate = 0.05;
                    const analysisYears = 20;
                    let maintenanceNpv = 0;
                    for (let yr = 1; yr <= analysisYears; yr++) {
                        const yearlyMaintenance = annualThroughput * maintenanceCostPerKwh;
                        maintenanceNpv += yearlyMaintenance / Math.pow(1 + discountRate, yr);
                    }

                    batt.degradation = {
                        annual_cycles: Math.round(fullCycles * 10) / 10,
                        annual_throughput_kwh: Math.round(annualThroughput),
                        years_to_80_pct: yearsTo80,
                        eol_capacity_pct: Math.round(Math.pow(1 - totalAnnualFade, analysisYears) * 1000) / 10,
                        maintenance_cost: Math.round(maintenanceNpv)
                    };
                    console.log('Enriched results with degradation:', batt.degradation);
                }

                // Compute survivability if not present
                const site = outputs?.Site || {};
                if (!site.survivability) {
                    const criticalLoadFraction = savedInputs?.ElectricLoad?.critical_load_fraction || 0.5;
                    const loadsKw = savedInputs?.ElectricLoad?.loads_kw || [];
                    const avgLoad = loadsKw.length > 0 ? loadsKw.reduce((a, b) => a + b, 0) / loadsKw.length : 0;
                    const criticalLoadKw = avgLoad * criticalLoadFraction;

                    const usableKwh = batt.size_kwh * 0.85;  // 85% usable
                    const minHours = criticalLoadKw > 0 ? usableKwh / criticalLoadKw : 0;

                    // PV contribution
                    const pvKw = outputs?.PV?.size_kw || 0;
                    const avgPvGen = pvKw * 0.15;  // ~15% capacity factor avg
                    const effectiveLoad = Math.max(0, criticalLoadKw - avgPvGen);
                    const avgHours = effectiveLoad > 0 ? (usableKwh / effectiveLoad) : minHours * 2;

                    outputs.Site = outputs.Site || {};
                    outputs.Site.survivability = {
                        min_hours: Math.round(minHours * 10) / 10,
                        avg_hours: Math.round(Math.min(avgHours, 168) * 10) / 10,
                        critical_load_met_pct: Math.round(minHours >= 4 ? 100 : (minHours / 4) * 100),
                        battery_kwh: batt.size_kwh,
                        critical_load_kw: Math.round(criticalLoadKw * 10) / 10
                    };
                    console.log('Enriched results with survivability:', outputs.Site.survivability);
                }
            }

            // Restore results and outputs
            setResults(savedResults);
            setOutputs(outputs);
            setStatus('Loaded from save');

            // Transform results for charts if transformer provided
            if (transformResults && savedResults) {
                const transformed = transformResults(savedResults);
                if (Array.isArray(transformed)) {
                    setDailyData(transformed);
                } else if (transformed && transformed.data) {
                    setDailyData(transformed.data);
                    if (transformed.isOffGrid !== undefined) {
                        setIsOffGrid(transformed.isOffGrid);
                    }
                    if (transformed.infeasibilityMetrics) {
                        setInfeasibilityMetrics(transformed.infeasibilityMetrics);
                    }
                }
            }

            // Store saved inputs hash for comparison
            if (savedInputsHash) {
                setLastRunInputs(savedInputsHash);
            }

            return true;
        } catch (err) {
            console.error('Error loading saved results:', err);
            return false;
        }
    }, []);

    /**
     * Reset optimization state
     */
    const resetOptimization = useCallback(() => {
        stopPolling();
        setRunUuid(null);
        setStatus("");
        setOutputs(null);
        setResults(null);
        setDailyData([]);
        setIsOffGrid(false);
        setInfeasibilityMetrics(null);
        setNeedsRerun(false);
    }, [stopPolling]);

    return {
        // Run state
        runUuid, setRunUuid,
        lastRunUuid, setLastRunUuid,
        manualRunUuid, setManualRunUuid,
        lastRunInputs, setLastRunInputs,

        // Status
        status, setStatus,
        statusPhase, setStatusPhase,
        isSubmitting,
        initialDelay, setInitialDelay,

        // Results
        outputs, setOutputs,
        results, setResults,
        dailyData, setDailyData,
        isOffGrid, setIsOffGrid,
        infeasibilityMetrics, setInfeasibilityMetrics,
        bauResults, setBauResults,  // BAU results for caching
        needsRerun, setNeedsRerun,

        // Loading
        loadResultsUuid, setLoadResultsUuid,
        isLoadingResults,

        // Actions
        submitScenario,
        startPolling,
        stopPolling,
        loadResults,
        loadSavedResults,
        resetOptimization,

        // Polling status
        isPolling: pollingRef.current !== null,
        isRunning,
    };
}

export default useOptimization;
